import { ReactNode, useState } from 'react'
import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react'

interface SidebarSectionProps {
  title: string
  icon?: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  collapsible?: boolean
  className?: string
  sidebarCollapsed?: boolean
  expandsUpward?: boolean
}

export function SidebarSection({ 
  title, 
  icon, 
  children, 
  defaultOpen = true, 
  collapsible = true,
  className,
  sidebarCollapsed = false,
  expandsUpward = false
}: SidebarSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  if (sidebarCollapsed) {
    return (
      <div className={cn("space-y-1", className)}>
        {children}
      </div>
    )
  }

  if (!collapsible) {
    return (
      <div className={cn("space-y-3", className)}>
        <div className="px-3 py-2">
          <div className="flex items-center gap-2">
            {icon && <div className="text-muted-foreground">{icon}</div>}
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          </div>
        </div>
        <div className="space-y-1">
          {children}
        </div>
      </div>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn("space-y-1", className)}>
      <CollapsibleTrigger className="w-full px-3 py-2 hover:bg-accent/50 rounded-lg transition-colors">
        <div className="flex items-center gap-2 text-left">
          {icon && <div className="text-muted-foreground">{icon}</div>}
          <h3 className="text-sm font-semibold text-foreground flex-1">{title}</h3>
          {isOpen ? (
            expandsUpward ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            expandsUpward ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
